import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { faMagnifyingGlass } from "@performyard/fontawesome/pro-regular-svg-icons";
import { ButtonLoading, ButtonStandard, InputText, Skeleton, Text, toast } from "@performyard/py-components";

import ContentModal from "../../../../components/Modal/Templates/ContentModal";
import GoalRowDisplay from "../components/GoalRowDisplay";

import { useAddMeetingAgendaItem, useSearchService } from "../../../../api/hooks";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";
import { useMeetingGoalsVisibility } from "../hooks";

import { SERVICE_GOAL } from "../../../../api/services/defs/search-service";
import type { Goal } from "../../../../api/services/searchServiceResultTypes/goal";

import { getMeetingGoalsSearchFilters } from "../utils";

import { config } from "../config";
import { useMeetingContext } from "../context";
import { meetingDetailsQueryKeys } from "../query-keys";

type AddGoalsToAgendaModalProps = {
	isOpen: boolean;
	onClose: () => void;
	existingGoalIds: string[];
};

const AddGoalsToAgendaModal = ({
	isOpen,
	onClose,
	existingGoalIds,
}: AddGoalsToAgendaModalProps): React.ReactElement => {
	const queryClient = useQueryClient();
	const { meeting, userId, isAdmin = false } = useMeetingContext();

	const [searchValue, setSearchValue] = useState("");
	const [selectedGoalIds, setSelectedGoalIds] = useState<string[]>([]);
	const [isSaving, setIsSaving] = useState(false);

	const existingGoalIdSet = useMemo(() => new Set(existingGoalIds), [existingGoalIds]);
	const debouncedSearchValue = useDebouncedValue(searchValue, 300);
	const meetingParticipantIds = useMemo(() => {
		return meeting.participants?.map((participant) => participant.userId).filter(Boolean) || [];
	}, [meeting.participants]);

	const { visibleAssigneeIds, isLoading: isVisibilityLoading } = useMeetingGoalsVisibility({
		currentUserId: userId,
		meetingParticipantIds,
		isAdmin,
	});
	const hasVisibleAssignees = visibleAssigneeIds.length > 0;

	const searchFilters = useMemo(
		() => getMeetingGoalsSearchFilters({ status: ["active"] }, debouncedSearchValue, visibleAssigneeIds, userId),
		[debouncedSearchValue, visibleAssigneeIds, userId]
	);

	const goalsQuery = useSearchService<Goal>({
		service: SERVICE_GOAL,
		filters: searchFilters,
		sort: { name: "end_date", direction: "desc" },
		limit: config.goals.listLimit,
		defaultErrorMessage: "There was an error getting goals",
		disabled: !isOpen || isVisibilityLoading || !hasVisibleAssignees,
	});
	const goals = hasVisibleAssignees ? goalsQuery.data?.results || [] : [];
	const isLoadingGoals = goalsQuery.isLoading || isVisibilityLoading;

	const { mutateAsync: addAgendaItem } = useAddMeetingAgendaItem();

	useEffect(() => {
		if (isOpen) {
			setSearchValue("");
			setSelectedGoalIds([]);
		}
	}, [isOpen]);

	const toggleGoalSelection = useCallback(
		(goalId: string): void => {
			if (existingGoalIdSet.has(goalId)) {
				return;
			}

			setSelectedGoalIds((prev) =>
				prev.includes(goalId) ? prev.filter((id) => id !== goalId) : [...prev, goalId]
			);
		},
		[existingGoalIdSet]
	);

	const handleAddGoals = async (): Promise<void> => {
		if (selectedGoalIds.length === 0 || isSaving) {
			return;
		}

		const goalLookup = new Map(goals.map((goal) => [goal._id, goal]));
		const goalsToAdd = selectedGoalIds
			.map((goalId) => goalLookup.get(goalId))
			.filter((goal): goal is Goal => Boolean(goal));

		if (goalsToAdd.length === 0) {
			return;
		}

		setIsSaving(true);

		try {
			const results = await Promise.allSettled(
				goalsToAdd.map((goal) =>
					addAgendaItem({
						data: {
							name: goal.name,
							goalId: goal._id,
							meetings: [{ meetingId: meeting._id, status: "incomplete" }],
						},
					})
				)
			);

			const successCount = results.filter((r) => r.status === "fulfilled").length;
			const failureCount = results.filter((r) => r.status === "rejected").length;

			await queryClient.invalidateQueries({
				queryKey: meetingDetailsQueryKeys.meetingAgendaItems(meeting._id),
			});

			if (failureCount === 0) {
				toast.success(`Added ${successCount} goal${successCount === 1 ? "" : "s"} to agenda`);
			} else if (successCount === 0) {
				toast.error("Failed to add goals to agenda");
			} else {
				toast.warning(`Added ${successCount} goal${successCount === 1 ? "" : "s"}, ${failureCount} failed`);
			}
			onClose();
		} catch (error) {
			toast.error("Failed to add goals to agenda");
		} finally {
			setIsSaving(false);
		}
	};

	const loadingContent = (
		<div className="flex h-full flex-col">
			{Array.from({ length: 7 }).map((_, index) => (
				<div
					key={index}
					className={`flex flex-1 items-center gap-2 px-2 py-1 ${
						index % 2 === 0 ? "bg-neutral-1" : "bg-white"
					}`}
				>
					<Skeleton className="h-4 w-4 shrink-0 rounded-sm" />
					<Skeleton className="h-6 w-6 shrink-0 rounded-full" />
					<div className="flex min-w-0 flex-1 flex-col gap-1">
						<Skeleton className="h-4 w-3/4 rounded" />
						<Skeleton className="h-3 w-1/2 rounded" />
					</div>
				</div>
			))}
		</div>
	);

	const goalsListContent = goals.map((goal, index) => (
		<GoalRowDisplay
			key={goal._id}
			goal={goal}
			index={index}
			isSelected={selectedGoalIds.includes(goal._id)}
			isDisabled={existingGoalIdSet.has(goal._id) || isSaving}
			onToggle={() => toggleGoalSelection(goal._id)}
		/>
	));

	const emptyContent = (
		<div className="flex items-center justify-center py-8">
			<Text size="paragraph-2" weight="semi-bold" color="neutral-7">
				No Goals
			</Text>
		</div>
	);

	const goalsContent = isLoadingGoals ? loadingContent : goals.length > 0 ? goalsListContent : emptyContent;

	return (
		<ContentModal
			isOpen={isOpen}
			handleClose={onClose}
			size="auto"
			title={
				<Text size="sub-heading" weight="semi-bold">
					Add Goals as Agenda Items
				</Text>
			}
			buttons={[
				<div className="w-fit min-w-[90px]" key="cancel">
					<ButtonStandard
						variant="outline"
						color="neutral"
						size="large"
						onClick={onClose}
						disabled={isSaving}
					>
						Cancel
					</ButtonStandard>
				</div>,
				<div className="w-fit min-w-[120px]" key="add">
					<ButtonLoading
						color="primary"
						size="large"
						loading={isSaving}
						disabled={selectedGoalIds.length === 0 || isSaving}
						onClick={handleAddGoals}
					>
						Add Goals{selectedGoalIds.length > 0 ? ` (${selectedGoalIds.length})` : ""}
					</ButtonLoading>
				</div>,
			]}
		>
			<div className="flex flex-col gap-4" style={{ width: 416, height: 380 }}>
				<InputText
					leftIcon={faMagnifyingGlass}
					value={searchValue}
					onChange={setSearchValue}
					placeholder="Search goals..."
					clearable
					disabled={isSaving}
				/>
				<div className="flex-1 overflow-y-auto">{goalsContent}</div>
			</div>
		</ContentModal>
	);
};

export default AddGoalsToAgendaModal;
